import { ReactElement, useCallback } from "react";
import { DragEndParams, RenderItem } from "react-native-draggable-flatlist";

import PlayerItem from "@/components/organisms/PlayerItem";
import DraggableListView from "./DraggableListView";

import { Spinner } from "@/components/atoms/Spinner";
import { WatchlistWatchlistMetaFragment } from "@/generated/graphql";
import { PlayerCardData } from "@/types/playerCardTypes";
import useMobileScreen from "@/hooks/useMobileScreen";

import type { ListRenderItem } from "@/components/atoms/FlashList";

/**
 * Props for the DraggableListContainer component
 */
interface DraggableList {
  loadingPlayers: boolean;
  heightRecommendation?: number;
  refreshing: boolean;
  players: PlayerCardData[];
  onLoadMore?: () => void;
  onRefresh: () => void;
  onDragEnd?: (params: DragEndParams<PlayerCardData>) => void;
  activeWatchlist?: WatchlistWatchlistMetaFragment | null;
}

/**
 * Container component for draggable player list with reordering support
 * @param root0 - The component props
 * @param root0.loadingPlayers - Whether player data is currently being fetched
 * @param root0.players - Array of watchlist player fragments to display
 * @param root0.refreshing - Whether the list is being refreshed via pull-to-refresh
 * @param root0.onDragEnd - Callback invoked when user completes a drag-and-drop reorder
 * @param root0.onLoadMore - Callback invoked when user scrolls to load more players
 * @param root0.onRefresh - Callback invoked when user pulls to refresh the list
 * @param root0.heightRecommendation - Optional recommended height for list items in pixels
 * @param root0.activeWatchlist - Currently selected watchlist or null if none selected
 */
const DraggableListContainer = ({
  loadingPlayers,
  players,
  refreshing,
  onDragEnd,
  onLoadMore,
  onRefresh,
  heightRecommendation,
  activeWatchlist,
}: DraggableList) => {
  const isSmallScreen = useMobileScreen();
  const renderListFooterComponent = useCallback(
    () =>
      loadingPlayers && !refreshing && players.length ? (
        <Spinner size="large" color="white" />
      ) : null,
    [loadingPlayers, refreshing, players.length]
  );

  const renderItem: RenderItem<PlayerCardData> = useCallback(
    ({ item, isActive, drag }) => (
      <PlayerItem
        isActive={isActive}
        drag={drag}
        item={item}
        watchlist={activeWatchlist}
      />
    ),
    [activeWatchlist]
  );

  const webRenderItem: ListRenderItem<PlayerCardData> = useCallback(
    ({ item }) =>
      renderItem({
        item,
        drag: () => {},
        isActive: false,
        getIndex: () => 0,
      }) as ReactElement,
    [renderItem]
  );

  return (
    <DraggableListView
      loading={loadingPlayers}
      refreshing={refreshing}
      players={players}
      heightRecommendation={heightRecommendation}
      isSmallScreen={isSmallScreen}
      onLoadMore={onLoadMore}
      onRefresh={onRefresh}
      onDragEnd={onDragEnd}
      renderItem={renderItem}
      webRenderItem={webRenderItem}
      renderListFooterComponent={renderListFooterComponent}
    />
  );
};

export default DraggableListContainer;
